/**
 * Client for the CORS-enabled `api.php` JSON sidecars exposed by e-hentai
 * and ExHentai.
 *
 * EhViewer's `EhEngine.kt` documents the rate caps mirrored here:
 *   - {@link MAX_REQUEST_SIZE} (25) gids per request
 *   - {@link MAX_SEQUENTIAL_REQUESTS} (5) requests dispatched in parallel
 *   - {@link REQUEST_INTERVAL} (5_000 ms) between waves of 5
 *
 * A per-tab queue is the compatibility fallback. Browsers with SharedWorker
 * support also coordinate permits across tabs before posting directly from
 * the user's network address. API requests intentionally omit source cookies.
 */

import { sleep } from '@/lib/rate-limit';
import { NetworkError, ParseError, RateLimited } from '@/lib/errors';
import { assertOk, type EhHost } from './errors';
import { rateLimitForTransport, reportEhRateLimit, waitForEhRequest } from './request-control';
import { API_E, API_EX, type SiteContext } from './urls';

/** Max gids per `gdata` request. EhViewer constant. */
export const MAX_REQUEST_SIZE = 25;
/** Max in-flight `api.php` POSTs per site. */
export const MAX_SEQUENTIAL_REQUESTS = 5;
/** Default min ms between waves of in-flight requests. */
export const REQUEST_INTERVAL = 5_000;
const API_TIMEOUT_MS = 20_000;
const DIRECT_API_URL_BY_SITE: Record<EhHost, string> = {
  ehentai: API_E,
  exhentai: API_EX,
};

/**
 * Live read of the inter-wave throttle so e2e / unit tests can speed the
 * queue up without rebuilding. In production this always returns
 * {@link REQUEST_INTERVAL}; tests set `globalThis.__EH_API_INTERVAL_MS__ = 0`
 * (or any positive number) via Playwright's `page.addInitScript` before the
 * SPA bundle runs. We only honor finite non-negative numbers — anything
 * else falls back to the polite 5-second default.
 */
function currentRequestInterval(): number {
  const override = (globalThis as { __EH_API_INTERVAL_MS__?: unknown }).__EH_API_INTERVAL_MS__;
  if (typeof override === 'number' && Number.isFinite(override) && override >= 0) {
    return override;
  }
  return REQUEST_INTERVAL;
}

/** Shape of a single gallery's metadata as returned by `gdata`. */
export interface GalleryMetadata {
  gid: number;
  token: string;
  archiver_key?: string;
  title: string;
  title_jpn?: string;
  category: string;
  thumb: string;
  uploader: string;
  posted: string;
  filecount: string;
  filesize: number;
  expunged: boolean;
  rating: string;
  torrentcount: string;
  /** Tag array shaped as `["namespace:name", ...]`. */
  tags: string[];
  parent_gid?: string;
  first_gid?: string;
  /** Some endpoints include `error` instead of a real row. */
  error?: string;
}

export interface GdataResponse {
  gmetadata: GalleryMetadata[];
}

/** A single `[gid, token, pageNumber]` triple for `gtoken`. */
export type PageListEntry = readonly [gid: number, token: string, page: number];

export interface GtokenResponseEntry {
  gid: number;
  page: number;
  /** Per-page token ("ptoken") used by the `/s/{ptoken}/{gid}-N` page. */
  token: string;
}
export interface GtokenResponse {
  tokenlist: GtokenResponseEntry[];
}

export interface ShowpageResponse {
  /** HTML chunk containing the `<img>` tag for the requested page. */
  i3: string;
  /** Image key for the page (same as input `imgkey` echoed back). */
  k?: string;
  /** Echo of the page number. */
  n?: string;
  /** Echo of the (optional) preview thumbnail blob. */
  i?: string;
}

/** Bag of post-able payload shapes the server accepts. */
type ApiPayload =
  | { method: 'gdata'; gidlist: Array<[number, string]>; namespace: 1 }
  | { method: 'gtoken'; pagelist: Array<[number, string, number]> }
  | {
      method: 'showpage';
      gid: number;
      page: number;
      imgkey: string;
      showkey: string;
    }
  | {
      method: 'rategallery';
      apiuid: number;
      apikey: string;
      gid: number;
      token: string;
      rating: number;
    }
  | {
      method: 'votecomment';
      apiuid: number;
      apikey: string;
      gid: number;
      token: string;
      comment_id: number;
      comment_vote: number;
    }
  | {
      method: 'taggallery';
      apiuid: number;
      apikey: string;
      gid: number;
      token: string;
      tags: string;
      vote: 1 | -1;
    };

interface QueueItem<T> {
  payload: ApiPayload;
  resolve: (value: T) => void;
  reject: (err: Error) => void;
}

/**
 * Per-site dispatch queue. Funnels arbitrary payloads through a
 * 5-parallel / 5s-spaced wave pump.
 */
class ApiPhpQueue {
  private readonly queue: QueueItem<unknown>[] = [];
  private draining = false;

  constructor(private readonly site: SiteContext) {}

  enqueue<T>(payload: ApiPayload): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      this.queue.push({
        payload,
        resolve: resolve as (value: unknown) => void,
        reject,
      });
      void this.drain();
    });
  }

  private async drain(): Promise<void> {
    if (this.draining) return;
    this.draining = true;
    try {
      while (this.queue.length > 0) {
        const wave = this.queue.splice(0, MAX_SEQUENTIAL_REQUESTS);
        const results = await Promise.allSettled(
          wave.map((item) => doPost(this.site, item.payload)),
        );
        for (let i = 0; i < wave.length; i++) {
          const item = wave[i];
          const result = results[i];
          if (!item || !result) continue;
          if (result.status === 'fulfilled') item.resolve(result.value);
          else
            item.reject(
              result.reason instanceof Error ? result.reason : new Error(String(result.reason)),
            );
        }
        if (this.queue.length > 0) {
          await sleep(currentRequestInterval());
        }
      }
    } finally {
      this.draining = false;
    }
  }
}

const queues = new Map<EhHost, ApiPhpQueue>();

function queueFor(site: SiteContext): ApiPhpQueue {
  let q = queues.get(site.id);
  if (!q) {
    q = new ApiPhpQueue(site);
    queues.set(site.id, q);
  }
  return q;
}

async function doPost(site: SiteContext, payload: ApiPayload): Promise<unknown> {
  const expectedApiUrl = DIRECT_API_URL_BY_SITE[site.id];
  if (site.api !== expectedApiUrl) {
    throw new NetworkError(`Direct API URL is not audited for ${site.id}`, { source: site.id });
  }

  await waitForEhRequest(site.id, 'api', currentRequestInterval());
  const timeoutController = new AbortController();
  const timeoutId = globalThis.setTimeout(() => timeoutController.abort(), API_TIMEOUT_MS);
  try {
    let response: Response;
    try {
      response = await fetch(site.api, {
        method: 'POST',
        mode: 'cors',
        credentials: 'omit',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(payload),
        signal: timeoutController.signal,
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      throw new NetworkError(`Direct API request failed for ${site.api}: ${message}`, {
        source: site.id,
        cause: error,
      });
    }

    let text: string;
    try {
      text = await assertOk(response, site.id);
    } catch (error) {
      if (!(error instanceof RateLimited)) throw error;
      const scopedError = rateLimitForTransport(error, 'api');
      reportEhRateLimit(site.id, 'api', scopedError);
      throw scopedError;
    }
    if (!text) {
      throw new NetworkError(`empty body from ${site.api}`, { source: site.id });
    }
    try {
      return JSON.parse(text);
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      throw new ParseError(`Malformed JSON from ${site.api}: ${message}`, {
        source: site.id,
        snippet: text.slice(0, 200),
      });
    }
  } finally {
    globalThis.clearTimeout(timeoutId);
  }
}

/**
 * Batch-fetch gallery metadata. Each call is internally chunked into groups
 * of {@link MAX_REQUEST_SIZE}; the returned array preserves the input order.
 *
 * @param gids   `[gid, token]` tuples.
 * @param site   active site context.
 */
export async function gdata(
  gids: Array<readonly [number, string]>,
  site: SiteContext,
): Promise<GdataResponse> {
  if (gids.length === 0) return { gmetadata: [] };
  const chunks: Array<Array<[number, string]>> = [];
  for (let i = 0; i < gids.length; i += MAX_REQUEST_SIZE) {
    chunks.push(gids.slice(i, i + MAX_REQUEST_SIZE).map(([g, t]) => [g, t]));
  }
  const q = queueFor(site);
  const results = await Promise.all(
    chunks.map((chunk) =>
      q.enqueue<GdataResponse>({ method: 'gdata', gidlist: chunk, namespace: 1 }),
    ),
  );
  const all: GalleryMetadata[] = [];
  for (const r of results) {
    if (!r || !Array.isArray(r.gmetadata)) continue;
    for (const row of r.gmetadata) {
      if (row && row.error) continue;
      all.push(row);
    }
  }
  return { gmetadata: all };
}

/**
 * Resolve a list of `[gid, token, page]` triples to per-page tokens
 * (ptokens). Used when we have a gid + page index but no scraped ptoken
 * yet — e.g. when restoring history mid-read.
 */
export async function gtoken(
  pagelist: PageListEntry[],
  site: SiteContext,
): Promise<GtokenResponse> {
  if (pagelist.length === 0) return { tokenlist: [] };
  const q = queueFor(site);
  // gtoken is not as rate-sensitive but we share the same wave; chunk same size.
  const chunks: Array<Array<[number, string, number]>> = [];
  for (let i = 0; i < pagelist.length; i += MAX_REQUEST_SIZE) {
    chunks.push(pagelist.slice(i, i + MAX_REQUEST_SIZE).map(([g, t, p]) => [g, t, p]));
  }
  const results = await Promise.all(
    chunks.map((chunk) => q.enqueue<GtokenResponse>({ method: 'gtoken', pagelist: chunk })),
  );
  const all: GtokenResponseEntry[] = [];
  for (const r of results) {
    if (!r || !Array.isArray(r.tokenlist)) continue;
    for (const e of r.tokenlist) all.push(e);
  }
  return { tokenlist: all };
}

/**
 * Resolve a single page's image URL via `showpage`. The response's `i3`
 * field is a JSON-escaped HTML chunk; the caller is expected to strip
 * backslashes and regex out the `<img id="img" src="...">` URL.
 *
 * This helper just returns the raw `ShowpageResponse`; URL extraction
 * lives in `image-flow.ts`.
 */
export async function showpage(
  args: { gid: number; page: number; imgkey: string; showkey: string },
  site: SiteContext,
): Promise<ShowpageResponse> {
  const q = queueFor(site);
  return q.enqueue<ShowpageResponse>({
    method: 'showpage',
    gid: args.gid,
    page: args.page,
    imgkey: args.imgkey,
    showkey: args.showkey,
  });
}

/**
 * STUB. Rate / tag / vote operations require an `apiuid` + `apikey`
 * pair scraped from the gallery detail HTML. We don't surface UI for them
 * yet; calling these throws so the path is loud if we hook them up later.
 */
export async function rategallery(
  _args: { apiuid: number; apikey: string; gid: number; token: string; rating: number },
  _site: SiteContext,
  _opts: { cookies?: string } = {},
): Promise<void> {
  throw new Error(
    'rategallery: not implemented yet — requires apiuid/apikey extraction from detail HTML.',
  );
}

/**
 * STUB — same status as {@link rategallery}.
 */
export async function votecomment(
  _args: {
    apiuid: number;
    apikey: string;
    gid: number;
    token: string;
    comment_id: number;
    comment_vote: number;
  },
  _site: SiteContext,
  _opts: { cookies?: string } = {},
): Promise<void> {
  throw new Error('votecomment: not implemented yet.');
}

/**
 * STUB — same status as {@link rategallery}.
 */
export async function taggallery(
  _args: { apiuid: number; apikey: string; gid: number; token: string; tags: string; vote: 1 | -1 },
  _site: SiteContext,
  _opts: { cookies?: string } = {},
): Promise<void> {
  throw new Error('taggallery: not implemented yet.');
}

/**
 * Strip backslashes from a JSON-escaped HTML chunk so a regex can match
 * across `\"` and `\/` escapes. EhViewer does this verbatim before scraping
 * the `i3` field for the actual image URL.
 */
export function unescapeJsonHtml(input: string): string {
  return input.replace(/\\/g, '');
}

/**
 * Visible for testing — chunk an arbitrary list into windows of size
 * {@link MAX_REQUEST_SIZE}.
 */
export function chunkForGdata<T>(items: readonly T[]): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += MAX_REQUEST_SIZE) {
    out.push(items.slice(i, i + MAX_REQUEST_SIZE) as T[]);
  }
  return out;
}
